import { useMemo, useState, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Form } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useAppContext } from '@/Main';
import { rgbaColor } from '@/helpers/utils';
import { getHelpHtml } from '../../utils/help/help-content';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';
import { DASHBOARD_PLOT_HEIGHT, chartChrome, escapeHtml, themePrimary } from '../../utils/competition-chart';
import { DashboardEmptyNote } from './DashboardEmptyNote';
import { ChartLegend, useLegendHidden } from './competition/ChartLegend';
import type { ProjectVisibilityModelBreakdown, ProjectVisibilityTopBrand } from '../../types';

const NO_COMPETITOR = '';

const METRIC_DEFS = [
  { key: 'visibilityScore', label: 'دیده‌شدن در AI', helpKey: 'ai_visibility' },
  { key: 'shareOfVoice', label: 'سهم از دیده‌شدن', helpKey: 'share_of_voice' },
] as const;

function metricColors(): Record<(typeof METRIC_DEFS)[number]['key'], string> {
  return { visibilityScore: themePrimary(), shareOfVoice: '#d97706' };
}

function brandKey(brand: { knownBrandId: string | null; canonicalName: string }): string {
  return brand.knownBrandId ?? brand.canonicalName;
}

type ChartBrand = { key: string; name: string; isFocus: boolean; isYou: boolean };

export function ModelBrandComparisonCard({
  models,
  competitors,
  footer,
  plotHeight = DASHBOARD_PLOT_HEIGHT,
  hideHeader,
  focusBrand,
}: {
  models: ProjectVisibilityModelBreakdown[];
  competitors: ProjectVisibilityTopBrand[];
  footer?: ReactNode;
  plotHeight?: number;
  hideHeader?: boolean;
  /** When set, this brand takes the own-brand slot (domain filter). */
  focusBrand?: { knownBrandId: string | null; canonicalName: string } | null;
}) {
  const [competitorKey, setCompetitorKey] = useState<string>(NO_COMPETITOR);
  const { hidden, toggle } = useLegendHidden();
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };

  const primary = useMemo((): ChartBrand | null => {
    if (focusBrand?.canonicalName) {
      return {
        key: brandKey(focusBrand),
        name: focusBrand.canonicalName,
        isFocus: true,
        isYou: false,
      };
    }
    for (const model of models) {
      const own = model.brands.find((brand) => brand.relationship === 'you');
      if (own) return { key: 'you', name: own.canonicalName, isFocus: true, isYou: true };
    }
    return null;
  }, [focusBrand, models]);

  const pickerCompetitors = useMemo(
    () => competitors.filter((brand) => !primary || brandKey(brand) !== primary.key),
    [competitors, primary],
  );
  const competitor = pickerCompetitors.find((brand) => brandKey(brand) === competitorKey) ?? null;

  const brands = useMemo(() => {
    const list: ChartBrand[] = [];
    if (primary) list.push(primary);
    if (competitor) {
      list.push({
        key: brandKey(competitor),
        name: competitor.canonicalName,
        isFocus: false,
        isYou: competitor.relationship === 'you',
      });
    }
    return list;
  }, [primary, competitor]);

  const findBrand = (model: ProjectVisibilityModelBreakdown, brand: ChartBrand) =>
    model.brands.find((item) =>
      brand.isYou
        ? item.relationship === 'you'
        : brandKey(item) === brand.key || item.canonicalName === brand.name,
    );

  const allMetrics = useMemo(() => metricColors(), [themeVariant]);
  const visibleMetrics = useMemo(
    () => METRIC_DEFS.filter((metric) => !hidden.has(metric.key)),
    [hidden],
  );

  const option = useMemo(() => {
    if (!models.length || !brands.length || !visibleMetrics.length) return null;
    const chrome = chartChrome();

    const bars = brands.flatMap((brand) =>
      visibleMetrics.map((metric) => ({
        brand,
        metric,
        color: brand.isFocus ? allMetrics[metric.key] : rgbaColor(allMetrics[metric.key], 0.5),
      })),
    );

    return {
      color: bars.map((bar) => bar.color),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: chrome.splitLine, opacity: 0.4 } },
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (params: Array<{ dataIndex: number }>) => {
          if (!params.length) return '';
          const model = models[params[0].dataIndex];
          const rows = brands
            .map((brand) => {
              const found = findBrand(model, brand);
              const metricRows = visibleMetrics
                .map(
                  (metric) => `
                  <div style="margin-top:2px">
                    <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${
                      brand.isFocus ? allMetrics[metric.key] : rgbaColor(allMetrics[metric.key], 0.5)
                    };margin-inline-end:6px"></span>
                    ${metric.label}: <strong>${found?.[metric.key] ?? 0}%</strong>
                  </div>`,
                )
                .join('');
              return `<div style="margin-top:6px"><div style="font-weight:600">${escapeHtml(brand.name)}${brand.isYou ? ' (شما)' : ''}</div>${metricRows}</div>`;
            })
            .join('');
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">${escapeHtml(model.modelName)}</div>
            <div style="color:${chrome.muted}">${model.responses} پاسخ</div>${rows}</div>`;
        },
      },
      legend: { show: false },
      grid: { left: 8, right: 40, top: 12, bottom: 32 },
      xAxis: {
        type: 'category',
        data: models.map((model) => model.modelName),
        axisLine: { lineStyle: { color: chrome.axisLine } },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          interval: 0,
          overflow: 'truncate',
          width: 80,
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        min: 0,
        max: (extent: { max: number }) =>
          Math.min(100, Math.max(10, Math.ceil(extent.max / 10) * 10)),
        splitNumber: 4,
        splitLine: { lineStyle: { color: chrome.splitLine } },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          formatter: '{value}%',
        },
      },
      series: bars.map((bar) => ({
        name: bar.brand.isFocus ? bar.metric.label : `${bar.metric.label} - ${bar.brand.name}`,
        type: 'bar',
        barMaxWidth: 18,
        barGap: '8%',
        barCategoryGap: '35%',
        itemStyle: { color: bar.color, borderRadius: [4, 4, 0, 0] },
        data: models.map((model) => findBrand(model, bar.brand)?.[bar.metric.key] ?? 0),
      })),
    };
  }, [models, brands, visibleMetrics, allMetrics, isDark, themeVariant]);

  const competitorPicker =
    pickerCompetitors.length > 0 ? (
      <Form.Select
        size="sm"
        className="fs--2"
        style={{ width: '9rem' }}
        aria-label="انتخاب رقیب برای مقایسه"
        value={competitor ? competitorKey : NO_COMPETITOR}
        onChange={(event) => setCompetitorKey(event.target.value)}
      >
        <option value={NO_COMPETITOR}>مقایسه با رقیب…</option>
        {pickerCompetitors.map((brand) => (
          <option key={brandKey(brand)} value={brandKey(brand)}>
            {brand.canonicalName}
          </option>
        ))}
      </Form.Select>
    ) : null;

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-3"
          title="مقایسه به تفکیک مدل AI"
          endEl={competitorPicker}
        />
      )}
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1">
        {hideHeader ? <div className="mb-2">{competitorPicker}</div> : null}
        {primary && !primary.isYou ? (
          <p className="mb-2 fs--2 fw-bold text-1000">{primary.name}</p>
        ) : null}
        {competitor ? (
          <p className="mb-2 fs--2 text-500">
            رنگ کم‌رنگ‌تر: {competitor.canonicalName}
          </p>
        ) : null}
        <div className="flex-grow-1 d-flex align-items-center">
          {!option ? (
            <DashboardEmptyNote minHeight={plotHeight}>
              در این بازه پاسخی از مدل‌های AI ثبت نشده.
            </DashboardEmptyNote>
          ) : (
            <ReactECharts
              key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
              option={option}
              notMerge
              className="w-100"
              style={{ height: plotHeight }}
              opts={{ renderer: 'svg' }}
            />
          )}
        </div>
        {models.length > 0 && brands.length > 0 ? (
          <ChartLegend
            onToggle={toggle}
            items={METRIC_DEFS.map((metric) => ({
              id: metric.key,
              label: metric.label,
              color: allMetrics[metric.key],
              hidden: hidden.has(metric.key),
              trailing: <HelpTooltip html={getHelpHtml(metric.helpKey)} />,
            }))}
          />
        ) : null}
      </Card.Body>
      {footer}
    </Card>
  );
}
